// Organizace (firma řemeslníka): zod schémata pro založení a nastavení (docs/SPEC.md 2.1 a 2.10).
import { z } from 'zod'
import {
  AI_ACTION_TYPES,
  aiActionTypeSchema,
  orgRoleSchema,
  tradeSchema,
  type AiActionType,
  type OrgRole,
} from './enums'

export const phoneInputSchema = z
  .string()
  .trim()
  .min(9, 'Zadej telefon')
  .max(20)
  .regex(/^[+\d][\d\s]{8,19}$/, 'Telefon zadej jako 777 123 456 nebo +420 777 123 456')

export const createOrgSchema = z.object({
  name: z.string().trim().min(2, 'Zadej název firmy').max(120),
  trade: tradeSchema.default('other'),
  phone: phoneInputSchema.optional().or(z.literal('')),
  city: z.string().trim().max(80).optional().or(z.literal('')),
})
export type CreateOrgInput = z.infer<typeof createOrgSchema>

export const AI_TONES = ['informal', 'formal'] as const
export const aiToneSchema = z.enum(AI_TONES)
export type AiTone = z.infer<typeof aiToneSchema>

/**
 * Úrovně důvěry Buddyho per typ akce (SPEC 2.7): výchozí všechno `manual`. Na `auto` jde přepnout jen
 * interní položky `create_task` a `schedule_reminder` (CLAUDE.md pravidlo 2), ostatní UI ukáže zamčené.
 */
export const AI_TRUST_AUTO_ALLOWED = [
  'create_task',
  'schedule_reminder',
] as const satisfies readonly AiActionType[]
export const AI_TRUST_LEVELS = ['manual', 'auto'] as const
export const aiTrustLevelSchema = z.enum(AI_TRUST_LEVELS)
export type AiTrustLevel = z.infer<typeof aiTrustLevelSchema>
export type AiTrust = Partial<Record<AiActionType, AiTrustLevel>>

export const aiTrustSchema = z
  .partialRecord(aiActionTypeSchema, aiTrustLevelSchema)
  .superRefine((value, ctx) => {
    for (const [type, level] of Object.entries(value)) {
      if (level === 'auto' && !(AI_TRUST_AUTO_ALLOWED as readonly string[]).includes(type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [type],
          message: 'Tuhle akci musí schvalovat člověk.',
        })
      }
    }
  })

/** Efektivní úroveň: chybějící klíč = manual. */
export function aiTrustLevel(trust: AiTrust | null | undefined, type: AiActionType): AiTrustLevel {
  return trust?.[type] ?? 'manual'
}

/** Všechny typy akcí v pořadí pro UI Nastavení → Buddy. */
export const AI_TRUST_ACTION_TYPES = AI_ACTION_TYPES

export const addressSchema = z.object({
  street: z.string().trim().max(120).optional(),
  city: z.string().trim().max(80).optional(),
  zip: z.string().trim().max(10).optional(),
  country: z.string().trim().max(2).optional(),
})

export const updateOrgSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  trade: tradeSchema.optional(),
  phone: phoneInputSchema.optional().or(z.literal('')),
  email: z.string().trim().email().optional().or(z.literal('')),
  city: z.string().trim().max(80).optional().or(z.literal('')),
  address: addressSchema.optional(),
  ico: z
    .string()
    .trim()
    .regex(/^\d{8}$/, 'IČO má 8 číslic')
    .optional()
    .or(z.literal('')),
  dic: z.string().trim().max(14).optional().or(z.literal('')),
  isVatPayer: z.boolean().optional(),
  timezone: z.string().trim().max(60).optional(),
  locale: z.enum(['cs', 'sk']).optional(),
  aiTone: aiToneSchema.optional(),
  aiTrust: aiTrustSchema.optional(),
})
export type UpdateOrgInput = z.infer<typeof updateOrgSchema>

/** GET a PATCH /v1/org: řádek org_settings + role přihlášeného. */
export interface OrgSettingsResponse {
  id: string
  orgId: string
  name: string
  trade: string
  timezone: string
  locale: string
  currency: string
  isVatPayer: boolean
  phoneE164: string | null
  email: string | null
  city: string | null
  address: { street?: string; city?: string; zip?: string; country?: string } | null
  ico: string | null
  dic: string | null
  aiTone: AiTone
  aiTrust: AiTrust
  /** Žádost o výmaz firmy (SPEC 2.10 Data): kdy a proč, null = žádná. */
  deleteRequestedAt: string | null
  deleteReason: string | null
  role: string
}

// Data (SPEC 2.10): export ZIP a žádost o výmaz firmy.

/** POST /v1/org/delete-request: majitel opíše název firmy jako potvrzení. */
export const deleteRequestSchema = z.object({
  confirmName: z.string().trim().min(1, 'Opiš název firmy'),
  reason: z.string().trim().max(1000).optional().or(z.literal('')),
})
export type DeleteRequestInput = z.infer<typeof deleteRequestSchema>

/** Obsah export.json v ZIPu z GET /v1/export (vedle CSV souborů a README.txt). */
export interface ExportManifest {
  exportedAt: string
  org: { id: string; name: string }
  counts: Record<string, number>
  /** Soubory příloh v ZIPu nejsou, jen jejich seznam (název, velikost, zakázka). */
  files: string[]
}

// Členové a pozvánky (SPEC 2.10 Členové, Better Auth plugin organization).

/** POST /v1/org/invitations */
export const inviteMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email('Zadej platný email'),
  role: orgRoleSchema.default('member'),
})
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>

/** PATCH /v1/org/members/:userId */
export const updateMemberRoleSchema = z.object({ role: orgRoleSchema })
export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>

export type InvitationStatus = 'pending' | 'accepted' | 'rejected' | 'canceled'

/** Řádek seznamu čekajících pozvánek firmy (GET /v1/org/invitations). */
export interface InvitationItem {
  id: string
  email: string
  role: OrgRole
  status: InvitationStatus
  expiresAt: string
  createdAt: string
  inviterName: string
}

/** GET /v1/invitations/:id pro stránku /pozvanka/:id (přihlášený uživatel, i cizí email). */
export interface InvitationDetail {
  id: string
  orgName: string
  inviterName: string
  email: string
  role: OrgRole
  status: InvitationStatus
  expiresAt: string
  /** Pozvánka je pro jiný email, než má přihlášený uživatel: přijmout nejde, jen odhlásit se. */
  forAnotherEmail: boolean
  /** Uživatel už je členem této firmy. */
  alreadyMember: boolean
}

/** GET /v1/me/invitations: čekající pozvánky pro email přihlášeného (ukazuje je i založení firmy). */
export interface MeInvitation {
  id: string
  orgName: string
  inviterName: string
  role: OrgRole
  expiresAt: string
}

/** Štíhlý tvar organizace pro menu profilu a přepínání firem. */
export interface OrgSummary {
  id: string
  name: string
  slug: string
  role: string
  trade: string
}
